import { useState, useEffect } from 'react';
import { getPinCustomers, getPinEntries, createPinEntry, updatePinEntry, deletePinEntry } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

export const PinEntry = ({ selectedDate }) => {
  const [pinCustomers, setPinCustomers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [custRes, entriesRes] = await Promise.all([
        getPinCustomers(),
        getPinEntries(selectedDate ? selectedDate.substring(0, 7) : ''),
      ]);
      setPinCustomers(custRes.data || []);
      setEntries(entriesRes.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (customer) => {
    try {
      const res = await createPinEntry({
        customer_id: customer.id,
        customer_name: customer.name,
        customer_code: customer.code,
        delivery_date: selectedDate || format(new Date(), 'yyyy-MM-dd'),
        amount: 0,
      });
      setEntries(prev => [res.data, ...prev]);
      toast.success(`${customer.name}のPIN伝票を作成しました`);
    } catch (error) {
      toast.error('作成に失敗しました');
    }
  };

  const handleUpdate = async (entryId, field, value) => {
    try {
      await updatePinEntry(entryId, { [field]: value });
      setEntries(prev => prev.map(e => e.id === entryId ? { ...e, [field]: value } : e));
    } catch (error) {
      toast.error('更新に失敗しました');
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await deletePinEntry(entryId);
      setEntries(prev => prev.filter(e => e.id !== entryId));
      toast.success('削除しました');
    } catch (error) {
      toast.error('削除に失敗しました');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-4" data-testid="pin-entry-section">
      {/* PIN入力用顧客の一覧 + 新規作成 */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">PIN入力用 取引先</CardTitle>
          <p className="text-sm text-slate-500">各取引先の「+」ボタンで売上伝票を作成</p>
        </CardHeader>
        <CardContent>
          {pinCustomers.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">PIN入力用の取引先が登録されていません（取引先マスタで設定してください）</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {pinCustomers.map(c => (
                <Button
                  key={c.id}
                  variant="outline"
                  size="sm"
                  onClick={() => handleAdd(c)}
                  className="flex items-center gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span className="font-mono text-xs">{c.code}</span>
                  <span>{c.name}</span>
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 入力済み伝票一覧 */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">PIN売上伝票一覧</CardTitle>
            <Badge variant="outline">{entries.length}件</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">PIN伝票はありません</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{minWidth: '850px'}}>
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    <th className="py-2 px-3 font-medium text-slate-500">取引先</th>
                    <th className="py-2 px-3 font-medium text-slate-500">コード</th>
                    <th className="py-2 px-3 font-medium text-slate-500">納品日</th>
                    <th className="py-2 px-3 font-medium text-slate-500">商品名</th>
                    <th className="py-2 px-3 font-medium text-slate-500">商品CD</th>
                    <th className="py-2 px-3 font-medium text-slate-500">数量</th>
                    <th className="py-2 px-3 font-medium text-slate-500 text-right">税抜金額</th>
                    <th className="py-2 px-3 font-medium text-slate-500">受注日時</th>
                    <th className="py-2 px-3 font-medium text-slate-500">CSV出力</th>
                    <th className="py-2 px-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(entry => (
                    <tr key={entry.id} className={`border-b border-slate-100 hover:bg-slate-50 ${!entry.exported_at ? 'bg-orange-50/60' : ''}`} data-testid={`pin-entry-row-${entry.id}`}>
                      <td className="py-2 px-3 font-medium">{entry.customer_name}</td>
                      <td className="py-2 px-3 font-mono text-xs text-slate-500">{entry.customer_code}</td>
                      <td className="py-2 px-3">
                        <Input
                          type="date"
                          value={entry.delivery_date || ''}
                          onChange={(e) => handleUpdate(entry.id, 'delivery_date', e.target.value)}
                          className="h-8 w-36"
                        />
                      </td>
                      <td className="py-2 px-3 text-slate-600">売上入力</td>
                      <td className="py-2 px-3 font-mono text-slate-500">333</td>
                      <td className="py-2 px-3 text-center">1</td>
                      <td className="py-2 px-3 text-right">
                        <Input
                          type="number"
                          value={entry.amount || ''}
                          onChange={(e) => handleUpdate(entry.id, 'amount', parseFloat(e.target.value) || 0)}
                          className="h-8 w-28 text-right ml-auto"
                          placeholder="¥0"
                        />
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-400">
                        {entry.created_at ? format(new Date(entry.created_at), 'M/d HH:mm', { locale: ja }) : '-'}
                      </td>
                      <td className="py-2 px-3 text-xs whitespace-nowrap">
                        {entry.exported_at ? (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-normal" data-testid={`pin-exported-${entry.id}`}>
                            ✓ 出力済 {format(new Date(entry.exported_at), 'M/d HH:mm', { locale: ja })}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 font-normal" data-testid={`pin-unexported-${entry.id}`}>
                            未出力
                          </Badge>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => handleDelete(entry.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
